import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'
import {
  CREATE_COMPANY_BUSINESS_GROUP,
  DELETE_COMPANY_BUSINESS_GROUP,
  GET_COMPANY_BUSINESS_GROUPS,
} from '../../graphql/companyGroups'

function formatDate(value) {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleDateString('es-CL')
  } catch {
    return '-'
  }
}

function GroupModal({
  title,
  onClose,
  onConfirm,
  confirmLabel = 'Guardar',
  confirmClass = 'btn-primary',
  saving = false,
  children,
}) {
  const handleSubmit = (e) => {
    e.preventDefault()
    onConfirm()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button type="button" className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">{children}</div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`btn ${confirmClass}`}
              disabled={saving}
            >
              {saving ? 'Guardando...' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function CompanyGroupsList({ embedded = false }) {
  const history = useHistory()
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const groupDetailPath = (id) => `/empresas/grupos/${id}`

  const [result, reexecute] = useQuery({
    query: GET_COMPANY_BUSINESS_GROUPS,
    requestPolicy: 'network-only',
  })
  const [, createGroup] = useMutation(CREATE_COMPANY_BUSINESS_GROUP)
  const [, deleteGroup] = useMutation(DELETE_COMPANY_BUSINESS_GROUP)

  const groups = useMemo(() => {
    const all = result.data?.companyBusinessGroups || []
    const q = search.trim().toLowerCase()
    if (!q) return all
    return all.filter(
      (group) =>
        String(group.name || '')
          .toLowerCase()
          .includes(q) ||
        String(group.description || '')
          .toLowerCase()
          .includes(q),
    )
  }, [result.data, search])

  const openCreate = () => {
    setError(null)
    setName('')
    setDescription('')
    setShowCreate(true)
  }

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('El nombre es requerido')
      return
    }
    setSaving(true)
    setError(null)
    const res = await createGroup({
      input: {
        name: trimmed,
        description: description.trim() || undefined,
      },
    })
    setSaving(false)
    if (res.error) {
      setError(res.error.message || 'No se pudo crear el grupo')
      return
    }
    setShowCreate(false)
    reexecute({ requestPolicy: 'network-only' })
    const createdId = res.data?.createCompanyBusinessGroup?.id
    if (createdId) history.push(groupDetailPath(createdId))
  }

  const handleDelete = async () => {
    if (!deleteTarget?.id) return
    setSaving(true)
    setError(null)
    const res = await deleteGroup({ id: deleteTarget.id })
    setSaving(false)
    if (res.error) {
      setError(res.error.message || 'No se pudo eliminar el grupo')
      setDeleteTarget(null)
      return
    }
    setDeleteTarget(null)
    reexecute({ requestPolicy: 'network-only' })
  }

  return (
    <>
      {!embedded ? (
        <div className="page-header">
          <h1 className="page-title">Grupos Empresariales</h1>
          <p className="page-subtitle">
            Crea grupos holding y asigna las empresas que pertenecen a cada uno
          </p>
        </div>
      ) : null}

      <div className="content-card">
        <div className="card-header">
          <h3 className="card-title">Lista de Grupos</h3>
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreate}>
            Crear grupo
          </button>
        </div>
        <div className="card-body">
          <div className="filters-container">
            <div className="filter-group">
              <label className="filter-label">BUSCAR</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Nombre o descripción..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {error && !showCreate && !deleteTarget ? (
            <div className="empty-state" style={{ padding: '0.75rem 0 1rem' }}>
              <p style={{ color: '#991b1b', margin: 0 }}>{error}</p>
            </div>
          ) : null}

          {result.fetching ? (
            <div className="loading-spinner">
              <div className="spinner" />
            </div>
          ) : result.error ? (
            <div className="empty-state">
              <p>{result.error.message || 'Error al cargar grupos'}</p>
            </div>
          ) : groups.length === 0 ? (
            <div className="empty-state">
              <p>
                {search.trim()
                  ? 'No se encontraron grupos con ese criterio'
                  : 'No hay grupos empresariales creados'}
              </p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>GRUPO</th>
                  <th>EMPRESAS</th>
                  <th>CREADO</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr
                    key={group.id}
                    className="clickable-row"
                    onClick={() => history.push(groupDetailPath(group.id))}
                  >
                    <td>
                      <div className="company-name">{group.name}</div>
                      {group.description ? (
                        <div className="company-alias">{group.description}</div>
                      ) : null}
                    </td>
                    <td className="text-center">{group.companiesCount ?? 0}</td>
                    <td>{formatDate(group.createdAt)}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => history.push(groupDetailPath(group.id))}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => {
                            setError(null)
                            setDeleteTarget(group)
                          }}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate ? (
        <GroupModal
          title="Crear grupo empresarial"
          onClose={() => setShowCreate(false)}
          onConfirm={handleCreate}
          confirmLabel="Crear"
          saving={saving}
        >
          <div className="form-group">
            <label className="form-label">Nombre del grupo</label>
            <input
              className="form-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Grupo Blanco"
              autoFocus
            />
          </div>
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label className="form-label">Descripción (opcional)</label>
            <textarea
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notas internas sobre el grupo"
              rows={3}
              style={{ height: 'auto', minHeight: '80px', resize: 'vertical' }}
            />
          </div>
          {error ? (
            <p style={{ color: '#991b1b', marginTop: '0.75rem', marginBottom: 0 }}>
              {error}
            </p>
          ) : null}
        </GroupModal>
      ) : null}

      {deleteTarget ? (
        <GroupModal
          title="Eliminar grupo"
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          confirmLabel="Eliminar"
          confirmClass="btn-warning"
          saving={saving}
        >
          <p style={{ margin: 0, color: 'var(--scfo-text)' }}>
            ¿Eliminar el grupo <strong>{deleteTarget.name}</strong>? Las empresas
            asociadas quedarán sin grupo empresarial.
          </p>
        </GroupModal>
      ) : null}
    </>
  )
}

export default CompanyGroupsList
